import * as React from 'react';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import CalendarMonthDateHeader from './surfaceItems/CalendarMonthDateHeader';
import CalendarMonthDayCell from './surfaceItems/CalendarMonthDayCell';
import CalendarMonthOverflowIndicator from './surfaceItems/CalendarMonthOverflowIndicator';
import CalendarMonthSurfaceItem from './surfaceItems/CalendarMonthSurfaceItem';
import CalendarWeekNumber from './surfaceItems/CalendarWeekNumber';
import MonthDragHelper from './surfaceItems/dragDrop/MonthDragHelper';
import type { CalendarMonthTransformProps } from './MonthSurfaceTransform';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { OwaDate, formatWeekDay, formatShortWeekDay, isSameDay } from 'owa-datetime';
import type { CalendarSurfaceStore, SurfaceItem } from 'owa-calendar-surface-dategrid';

/**
 * This file defines templates we are exposing to consumers
 * Use SurfaceTemplates.tsx for internal templates not exposed to consumer
 */

/**
 * Default template for date header in month view
 * @param key: any unique key for react
 * @param displayDate: date header model
 * @param style the css styling to apply
 */
export function createMonthColumnHeader(
    date: OwaDate,
    key: number,
    shouldShowFullWeekDay: boolean
) {
    return (
        <CalendarMonthDateHeader
            key={key}
            header={shouldShowFullWeekDay ? formatWeekDay(date) : formatShortWeekDay(date)}
        />
    );
}

/** Template for day cells in the month view */
export function createCalendarMonthDayCell(
    displayDate: OwaDate,
    isSelected: boolean,
    hideTodayEmphasis: boolean,
    dayId: string,
    dateText: string,
    isToday: boolean,
    isPastDate: boolean,
    renderDateHeaderAddOn?: (date: OwaDate) => JSX.Element
) {
    return (
        <CalendarMonthDayCell
            key={displayDate.toString()}
            dateText={dateText}
            dayId={dayId}
            hideTodayEmphasis={hideTodayEmphasis}
            isSelected={isSelected}
            isToday={isToday}
            isPastDate={isPastDate}>
            {!!renderDateHeaderAddOn && renderDateHeaderAddOn(displayDate)}
        </CalendarMonthDayCell>
    );
}

/** Template for week numbers in the month view */
export function createCalendarWeekNumber(weekNumber: number, weeksInMonth: number) {
    return (
        <CalendarWeekNumber
            weekNumber={weekNumber}
            weeksInMonth={weeksInMonth}
            key={'calendarWeekNumber_' + weekNumber}
        />
    );
}

/**
 * Default item template for month view
 * @param item: surface item
 * @param content: getter function for providing content
 * @param store: calendar surface store to pass user options
 */
export let monthItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    itemState?: any,
    increaseFontSize?: boolean,
    joinButtonVisible?: boolean
): JSX.Element => {
    return (
        <CalendarMonthSurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={colorScheme}
            charmId={charmId}
            itemState={{
                week: itemState.week,
            }}
            userOptions={store.userOptions}
            dateRange={store.dateRange}
            increaseFontSize={increaseFontSize}
            joinButtonVisible={joinButtonVisible}
            isSelectedDay={isSameDay(model.Start, store.selectedDate)}
        />
    );
};

/**
 * Template for overflow month item
 */
export let overflowMonthItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent
): JSX.Element => {
    return (
        <CalendarMonthOverflowIndicator
            key={model.ItemId.Id}
            item={model}
            isSelectedDay={isSameDay(model.Start, store.selectedDate)}
        />
    );
};

export let monthDragHelperTemplate: (
    store: CalendarSurfaceStore,
    config: CalendarMonthTransformProps,
    weekItems: SurfaceItem[][][]
) => JSX.Element = (store, config, weekItems): JSX.Element => {
    return (
        <MonthDragHelper
            surfaceStore={store}
            config={config}
            allWeekItems={weekItems}
            key="monthDragHelper"
        />
    );
};
